import { DEG, bvToRgb } from '@/lib/astro/sky'
import { CONSTELLATION_STORIES, STAR_NAMES } from '@/constants/constellations'

export type StarCatalogue = {
    count: number
    /** Right ascension in degrees, 0 to 360. */
    ra: Float32Array
    dec: Float32Array
    mag: Float32Array
    hip: Int32Array
    /** J2000 unit vectors, three numbers per star. */
    xyz: Float32Array
    /** Linear RGB tint, three numbers per star. */
    rgb: Float32Array
}

export type ConstellationFigure = {
    id: string
    name: string
    rank: number
    story: string
    /** Polylines of J2000 unit vectors. */
    lines: [number, number, number][][]
    /** Unit vector where the label sits. */
    anchor: [number, number, number]
}

type StarFeature = {
    id: number
    properties: { mag: number, bv: string | number }
    geometry: { coordinates: [number, number] }
}

type LineFeature = {
    id: string
    properties: { rank: string }
    geometry: { coordinates: [number, number][][] }
}

type NameFeature = {
    id: string
    properties: { name: string, rank: string, display?: [number, number, number] }
    geometry: { coordinates: [number, number] }
}

let starsPromise: Promise<StarCatalogue> | null = null
let constellationsPromise: Promise<ConstellationFigure[]> | null = null

const toUnit = (lon: number, lat: number): [number, number, number] => {
    const ra = (lon < 0 ? lon + 360 : lon) * DEG
    const dec = lat * DEG
    const c = Math.cos(dec)

    return [c * Math.cos(ra), c * Math.sin(ra), Math.sin(dec)]
}

const parseStars = (features: StarFeature[]): StarCatalogue => {
    const sorted = [...features].sort((a, b) => a.properties.mag - b.properties.mag)
    const count = sorted.length
    const catalogue: StarCatalogue = {
        count,
        ra: new Float32Array(count),
        dec: new Float32Array(count),
        mag: new Float32Array(count),
        hip: new Int32Array(count),
        xyz: new Float32Array(count * 3),
        rgb: new Float32Array(count * 3)
    }

    sorted.forEach((feature, index) => {
        const [lon, lat] = feature.geometry.coordinates
        const bv = Number(feature.properties.bv)
        const [x, y, z] = toUnit(lon, lat)
        const [r, g, b] = bvToRgb(Number.isFinite(bv) ? bv : 0.6)

        catalogue.ra[index] = lon < 0 ? lon + 360 : lon
        catalogue.dec[index] = lat
        catalogue.mag[index] = feature.properties.mag
        catalogue.hip[index] = feature.id
        catalogue.xyz[index * 3] = x
        catalogue.xyz[index * 3 + 1] = y
        catalogue.xyz[index * 3 + 2] = z
        catalogue.rgb[index * 3] = r
        catalogue.rgb[index * 3 + 1] = g
        catalogue.rgb[index * 3 + 2] = b
    })

    return catalogue
}

/** The naked-eye sky: about five thousand stars brighter than magnitude 6, brightest first. */
export const loadStars = (): Promise<StarCatalogue> => {
    if (!starsPromise) {
        starsPromise = fetch('/data/stars.6.json')
            .then(response => {
                if (!response.ok) throw new Error(`Star catalogue unavailable (${response.status})`)

                return response.json() as Promise<{ features: StarFeature[] }>
            })
            .then(json => parseStars(json.features))
            .catch(error => {
                starsPromise = null
                throw error
            })
    }

    return starsPromise
}

export const starName = (hip: number): string | undefined => STAR_NAMES[hip]

export const loadConstellations = (): Promise<ConstellationFigure[]> => {
    if (!constellationsPromise) {
        constellationsPromise = Promise.all([
            fetch('/data/constellations.lines.json').then(response => {
                if (!response.ok) throw new Error('Constellation lines unavailable')

                return response.json() as Promise<{ features: LineFeature[] }>
            }),
            fetch('/data/constellations.json').then(response => {
                if (!response.ok) throw new Error('Constellation names unavailable')

                return response.json() as Promise<{ features: NameFeature[] }>
            })
        ])
            .then(([lines, names]) => {
                const nameById = new Map(names.features.map(feature => [feature.id, feature]))

                return lines.features.map(feature => {
                    const meta = nameById.get(feature.id)
                    const display = meta?.properties.display
                    const anchor = display
                        ? toUnit(display[0], display[1])
                        : meta
                            ? toUnit(meta.geometry.coordinates[0], meta.geometry.coordinates[1])
                            : toUnit(feature.geometry.coordinates[0][0][0], feature.geometry.coordinates[0][0][1])

                    return {
                        id: feature.id,
                        name: meta?.properties.name ?? feature.id,
                        rank: Number(feature.properties.rank) || 3,
                        story: CONSTELLATION_STORIES[feature.id] ?? '',
                        lines: feature.geometry.coordinates.map(line => line.map(([lon, lat]) => toUnit(lon, lat))),
                        anchor
                    }
                })
            })
            .catch(error => {
                constellationsPromise = null
                throw error
            })
    }

    return constellationsPromise
}
